import { useContext } from 'react';
import { configurationContext } from '../contexts';
import { usePersonalMenuRootQuery, usePersonalMenuSubitemsQuery, useOrisalesLinksQuery } from '../queries';
import { mergeEshop, isOrisalesAvailable, mapToInternalMenuItems, findSelectedPath } from '../utils';
import { useLocalCacheStore } from './useLocalCacheStore';
var getInitialUser = function getInitialUser(initialItems) {
  return initialItems ? {
    FirstName: initialItems.CustomerFirstName,
    LastName: initialItems.CustomerLastName,
    UserType: initialItems.UserType,
    UserHash: initialItems.UserHash
  } : null;
};
var getInitialItems = function getInitialItems(initialItems) {
  return initialItems ? initialItems.Items.map(function (item) {
    return {
      Title: item.Title,
      Url: item.Url,
      Icon: item.Icon
    };
  }) : null;
};
var getNewItems = function getNewItems(subItems, rootItems, initialItems) {
  var _ref2, _getInitialItems;
  var _ref = (_ref2 = subItems !== null && subItems !== void 0 ? subItems : rootItems) !== null && _ref2 !== void 0 ? _ref2 : {
      items: (_getInitialItems = getInitialItems(initialItems)) !== null && _getInitialItems !== void 0 ? _getInitialItems : []
    },
    items = _ref.items;
  return mapToInternalMenuItems('', items);
};
export var useMenuItemsStore = function useMenuItemsStore() {
  var _initialItems$UserHas, _initialItems$OrderTy, _initialItems$UseCach, _ref3, _userHash, _rootItems$user, _initialItems$UserTyp, _rootItems$user2, _ref4, _ref5;
  // Get initial data from configuration if there are any
  var _useContext = useContext(configurationContext),
    customerId = _useContext.customerId,
    consultantNumber = _useContext.consultantNumber,
    initialItems = _useContext.personalMenuInitialData,
    consultantType = _useContext.consultantType,
    reportsItemName = _useContext.reportsItemName;
  var userHash = (_initialItems$UserHas = initialItems === null || initialItems === void 0 ? void 0 : initialItems.UserHash) !== null && _initialItems$UserHas !== void 0 ? _initialItems$UserHas : null;
  var orderType = (_initialItems$OrderTy = initialItems === null || initialItems === void 0 ? void 0 : initialItems.OrderType) !== null && _initialItems$OrderTy !== void 0 ? _initialItems$OrderTy : null;
  var useCaching = (_initialItems$UseCach = initialItems === null || initialItems === void 0 ? void 0 : initialItems.UseCaching) !== null && _initialItems$UseCach !== void 0 ? _initialItems$UseCach : true;

  // If there are no initial data we must call api to get current userHash
  var canFetchRootItems = userHash === null;
  var _usePersonalMenuRootQ = usePersonalMenuRootQuery(canFetchRootItems),
    rootItems = _usePersonalMenuRootQ.data;

  // User hash comes from initial data or from rootItem later
  userHash = (_ref3 = (_userHash = userHash) !== null && _userHash !== void 0 ? _userHash : rootItems === null || rootItems === void 0 ? void 0 : (_rootItems$user = rootItems.user) === null || _rootItems$user === void 0 ? void 0 : _rootItems$user.UserHash) !== null && _ref3 !== void 0 ? _ref3 : null;

  // Load data from cache if possible
  // Store will return (and do) nothing until we have userhash (and enabled caching)
  var _useLocalCacheStore = useLocalCacheStore(useCaching, customerId, userHash, orderType),
    cachedUser = _useLocalCacheStore.user,
    cachedSitecoreItems = _useLocalCacheStore.sitecoreItems,
    cachedEshopItems = _useLocalCacheStore.eshopItems,
    cachedMergedItems = _useLocalCacheStore.mergedItems,
    setCachedUser = _useLocalCacheStore.setCachedUser,
    setCachedSirecoreItems = _useLocalCacheStore.setCachedSirecoreItems,
    setCachedEshopItems = _useLocalCacheStore.setCachedEshopItems,
    setCachedMergedItems = _useLocalCacheStore.setCachedMergedItems;

  // Subitems can be fetched when they are not in cache and we have userhash (from root or initial items)
  var canFetchSubItems = userHash !== null && !cachedSitecoreItems && !cachedMergedItems;
  var _usePersonalMenuSubit = usePersonalMenuSubitemsQuery(canFetchSubItems, userHash),
    subitems = _usePersonalMenuSubit.data;

  // Orisales can be fetched when its not in cache and we know correct user type
  var userType = (_initialItems$UserTyp = initialItems === null || initialItems === void 0 ? void 0 : initialItems.UserType) !== null && _initialItems$UserTyp !== void 0 ? _initialItems$UserTyp : rootItems === null || rootItems === void 0 ? void 0 : (_rootItems$user2 = rootItems.user) === null || _rootItems$user2 === void 0 ? void 0 : _rootItems$user2.UserType;
  var canFetchOrisales = userType !== undefined && consultantNumber !== undefined && isOrisalesAvailable(userType) && !cachedEshopItems && !cachedMergedItems;
  var _useOrisalesLinksQuer = useOrisalesLinksQuery(canFetchOrisales),
    eshopData = _useOrisalesLinksQuer.data;

  // Put things together and save to cache
  var user = (_ref4 = (_ref5 = cachedUser !== null && cachedUser !== void 0 ? cachedUser : subitems === null || subitems === void 0 ? void 0 : subitems.user) !== null && _ref5 !== void 0 ? _ref5 : rootItems === null || rootItems === void 0 ? void 0 : rootItems.user) !== null && _ref4 !== void 0 ? _ref4 : getInitialUser(initialItems);
  if (!cachedUser && subitems !== null && subitems !== void 0 && subitems.user) {
    setCachedUser(subitems.user);
  }
  var eshopItems = cachedEshopItems !== null && cachedEshopItems !== void 0 ? cachedEshopItems : eshopData;
  if (!cachedEshopItems && eshopData !== null && eshopData !== void 0 && eshopData.Success) {
    setCachedEshopItems(eshopData);
  }
  var menuItems;
  if (cachedMergedItems) {
    // Everything comes from cache - we are done
    menuItems = cachedMergedItems;
  } else if (cachedSitecoreItems) {
    // We just have sitecore items, we might be missing orisales items
    menuItems = cachedSitecoreItems;
    if (eshopItems !== null && eshopItems !== void 0 && eshopItems.Success) {
      mergeEshop(menuItems, eshopItems, consultantType, reportsItemName);
      setCachedMergedItems(menuItems);
    }
  } else {
    // Cache is empty - we must get all from api responses
    menuItems = getNewItems(subitems, rootItems, initialItems);
    if (subitems) {
      setCachedSirecoreItems(menuItems);
      if (eshopItems !== null && eshopItems !== void 0 && eshopItems.Success) {
        mergeEshop(menuItems, eshopItems, consultantType, reportsItemName);
        setCachedMergedItems(menuItems);
      }
    }
  }
  return {
    user: user,
    items: menuItems,
    hasSubitems: cachedMergedItems !== null || cachedSitecoreItems !== null || subitems !== undefined,
    selectedPath: findSelectedPath(menuItems)
  };
};